import api, { STORAGE_KEYS } from './api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';

/**
 * Reports Service (Admin)
 * Handles admin reports API calls
 */
const reportsService = {
    /**
     * Get reports data
     * @param {string} dateRange - Filter: week, month, quarter, year
     * @returns {Promise<Object>}
     */
    getReports: async (dateRange = 'month') => {
        return api.get(`/admin/reports?dateRange=${dateRange}`);
    },

    /**
     * Get users report
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getUsersReport: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/reports/users${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get offers report
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getOffersReport: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/reports/offers${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get requests report
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getRequestsReport: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/reports/requests${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get subscriptions report
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getSubscriptionsReport: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/admin/reports/subscriptions${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Export report as CSV - downloads file directly
     * @param {string} reportType - Type of report (users, offers, requests, subscriptions, overview)
     * @param {Object} params - Query parameters (startDate, endDate)
     */
    exportReport: async (reportType, params = {}) => {
        try {
            const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
            const queryParams = new URLSearchParams(params);
            if (token) {
                queryParams.append('token', token);
            }
            const url = `${API_BASE_URL}/admin/reports/export/${reportType}?${queryParams.toString()}`;
            
            // Fetch the file
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to export report');
            }

            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `${reportType}_report.csv`;
            if (contentDisposition) {
                const matches = /filename="([^"]+)"/.exec(contentDisposition);
                if (matches && matches[1]) {
                    filename = matches[1];
                }
            }

            // Convert response to blob
            const blob = await response.blob();

            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            return { success: true };
        } catch (error) {
            console.error('Export error:', error);
            throw error;
        }
    }
};

export default reportsService;
